import React from 'react';
import {
  Autocomplete,
  TextField,
  Box
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FreelancerDeclineReason } from '../models/enums/FreelancerDeclineReason';
import { ClientDeclineReason } from '../models/enums/ClientDeclineReason';

interface DeclineReasonSelectorProps {
  type: 'freelancer' | 'client';
  reason: number | null;
  reasonDetails: string;
  onReasonChange: (reason: number | null) => void;
  onReasonDetailsChange: (details: string) => void;
  error?: string;
}

const DeclineReasonSelector: React.FC<DeclineReasonSelectorProps> = ({
  type,
  reason,
  reasonDetails,
  onReasonChange,
  onReasonDetailsChange,
  error
}) => {
  const { t } = useTranslation();

  const freelancerReasons: Array<{ value: number; label: string }> = [
    { value: FreelancerDeclineReason.NotAvailable, label: t('proposal.decline.freelancer.notAvailable') },
    { value: FreelancerDeclineReason.NotInterested, label: t('proposal.decline.freelancer.notInterested') },
    { value: FreelancerDeclineReason.ClientNotInterested, label: t('proposal.decline.freelancer.clientNotInterested') },
    { value: FreelancerDeclineReason.MissingSkills, label: t('proposal.decline.freelancer.missingSkills') },
    { value: FreelancerDeclineReason.Other, label: t('proposal.decline.freelancer.other') }
  ];

  const clientReasons: Array<{ value: number; label: string }> = [
    { value: ClientDeclineReason.BetterCandidateFound, label: t('proposal.decline.client.betterCandidateFound') },
    { value: ClientDeclineReason.OutOfBudget, label: t('proposal.decline.client.outOfBudget') },
    { value: ClientDeclineReason.SkillsMismatch, label: t('proposal.decline.client.skillsMismatch') },
    { value: ClientDeclineReason.InsufficientExperience, label: t('proposal.decline.client.insufficientExperience') },
    { value: ClientDeclineReason.LackOfProjectUnderstanding, label: t('proposal.decline.client.lackOfProjectUnderstanding') },
    { value: ClientDeclineReason.NoConcreteSamples, label: t('proposal.decline.client.noConcreteSamples') },
    { value: ClientDeclineReason.UnsatisfactoryCastingAnswers, label: t('proposal.decline.client.unsatisfactoryCastingAnswers') },
    { value: ClientDeclineReason.Other, label: t('proposal.decline.client.other') }
  ];

  const reasons = type === 'freelancer' ? freelancerReasons : clientReasons;
  const selectedReason = reasons.find(r => r.value === reason);
  const showOtherField = reason === (type === 'freelancer' ? FreelancerDeclineReason.Other : ClientDeclineReason.Other);

  return (
    <Box sx={{ mt: 2, mb: 3 }}>
      <Autocomplete
        options={reasons}
        value={selectedReason || null}
        onChange={(_, newValue) => onReasonChange(newValue?.value || null)}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('proposal.decline.selectReason')}
            error={!!error}
            helperText={error}
            required
          />
        )}
        fullWidth
      />

      {showOtherField && (
        <TextField
          fullWidth
          multiline
          rows={3}
          label={t('proposal.decline.reasonDetails')}
          value={reasonDetails}
          onChange={(e) => onReasonDetailsChange(e.target.value)}
          required
          sx={{ mt: 2 }}
          placeholder={t('proposal.decline.reasonDetailsPlaceholder')}
        />
      )}
    </Box>
  );
};

export default DeclineReasonSelector;
